import React, { useState, useEffect } from 'react';

import { COOKIE_SETTINGS_EVENT, readCookieConsent, saveCookieConsent, type CookiePreferences } from '../utils/cookieConsent';

const CookieConsent: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [customize, setCustomize] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({ analytics: false, advertising: false });

    useEffect(() => {
        const consent = readCookieConsent();
        setPreferences({ analytics: consent.analytics, advertising: consent.advertising });
        const timer = !consent.decided ? setTimeout(() => setVisible(true), 1200) : undefined;
        const reopen = () => {
            if (timer) clearTimeout(timer);
            const current = readCookieConsent();
            setPreferences({ analytics: current.analytics, advertising: current.advertising });
            setCustomize(true);
            setVisible(true);
        };
        window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
        };
    }, []);

    const choose = (choice: CookiePreferences) => {
        saveCookieConsent(choice);
        setPreferences(choice);
        setVisible(false);
        setCustomize(false);
    };

    if (!visible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
            {/* Backdrop blur overlay */}
            <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent h-20 pointer-events-none" />
            <div role="dialog" aria-labelledby="fc-cookie-heading" className="fc-cookie-consent max-h-[85vh] overflow-y-auto bg-[#0f1a2e]/95 backdrop-blur-xl border-t border-cyan-500/30 shadow-[0_-4px_30px_rgba(0,200,255,0.15)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                        {/* Cookie icon & text */}
                        <div className="flex-1 flex items-start gap-3">
                            <span className="text-3xl mt-0.5 flex-shrink-0">🍪</span>
                            <div>
                                <h3 id="fc-cookie-heading" className="text-white font-bold text-base sm:text-lg mb-1">
                                    Ta strona używa plików cookies
                                </h3>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    Niezbędne cookies zapewniają działanie koszyka i logowania.
                                    Za Twoją zgodą korzystamy z Google Analytics do analizy ruchu oraz Google Ads
                                    do pomiaru skuteczności i personalizacji reklam, w tym remarketingu.
                                    Możesz wybrać kategorie, odmówić lub później zmienić zgodę przez „Ustawienia cookies” w stopce.
                                    Więcej informacji znajdziesz w naszej{' '}
                                    <a
                                        href="/privacy"
                                        className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.history.pushState({}, '', '/privacy');
                                            window.dispatchEvent(new PopStateEvent('popstate'));
                                        }}
                                    >
                                        Polityce Prywatności
                                    </a>.
                                </p>
                                {customize && (
                                    <fieldset className="mt-3 space-y-3 text-sm text-gray-200">
                                        <legend className="font-semibold mb-2">Opcjonalne cookies</legend>
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <input type="checkbox" className="mt-1" checked={preferences.analytics}
                                                onChange={e => setPreferences(p => ({ ...p, analytics: e.target.checked }))} />
                                            <span>Analityka — statystyki odwiedzin i zachowania w sklepie (Google Analytics).</span>
                                        </label>
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <input type="checkbox" className="mt-1" checked={preferences.advertising}
                                                onChange={e => setPreferences(p => ({ ...p, advertising: e.target.checked }))} />
                                            <span>Reklamy — pomiar reklam, przekazywanie danych do Google w celach reklamowych,
                                                personalizacja i remarketing (Google Ads).</span>
                                        </label>
                                    </fieldset>
                                )}
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 w-full lg:w-auto lg:max-w-sm flex-shrink-0">
                            <button onClick={() => setCustomize(true)}
                                className="px-5 py-2.5 rounded-lg border border-gray-500 text-gray-200 bg-gray-800/50 text-sm font-medium whitespace-nowrap">
                                Dostosuj
                            </button>
                            {customize && (
                                <button onClick={() => choose(preferences)}
                                    className="px-5 py-2.5 rounded-lg border border-gray-500 text-gray-200 bg-gray-800/50 text-sm font-medium whitespace-nowrap">
                                    Zapisz wybrane
                                </button>
                            )}
                            <button
                                onClick={() => choose({ analytics: false, advertising: false })}
                                className="px-5 py-2.5 rounded-lg border border-gray-500 text-gray-300 hover:text-white hover:border-gray-400 bg-gray-800/50 hover:bg-gray-700/50 transition-all text-sm font-medium whitespace-nowrap"
                            >
                                Tylko niezbędne
                            </button>
                            <button
                                onClick={() => choose({ analytics: true, advertising: true })}
                                className="fc-cookie-accept px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all text-sm whitespace-nowrap"
                            >
                                Akceptuję wszystkie
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Slide-up animation */}
            <style>{`
                @keyframes slideUp {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                .animate-slide-up {
                    animation: slideUp 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default CookieConsent;
